import { Fragment, type ReactNode } from "react";
import { Link } from "react-router";
import { Button, Reveal } from "@rekati/ui";
import { cn, initials } from "@rekati/utils";
import IconArrow from "~icons/solar/arrow-right-linear";
import IconChat from "~icons/solar/chat-round-call-linear";
import IconGift from "~icons/solar/gift-linear";
import IconMap from "~icons/solar/map-point-hospital-linear";
import IconOpen from "~icons/solar/square-arrow-right-up-linear";
import { MURAL_WALL, type MuralIcon } from "../../assets/mural";
import {
  ABOUT,
  ABOUT_HIGHLIGHTS,
  CONTACT,
  PERKS,
  SHOW_DOORPRIZE,
  SPEAKERS,
  THEME_LINE,
  type Speaker,
} from "../../content/event";
import { useI18n } from "../../content/i18n";
import type { EventFacts } from "./queries";
import { SignalCable } from "./SignalCable";
import { Sticker } from "./Sticker";

/** A section label: a numbered yellow tab, so the page reads like a printed programme. */
function Kicker({
  children,
  n,
  id,
  className,
}: {
  children: ReactNode;
  n: string;
  id?: string;
  className?: string;
}) {
  return (
    <h2
      id={id}
      className={cn(
        "inline-flex w-fit items-center gap-2 font-mono text-xs font-medium tracking-[0.18em] uppercase",
        className,
      )}
    >
      <span
        className="grid h-6 min-w-8 place-items-center rounded-full bg-volt px-1.5 text-[0.68rem] text-volt-foreground"
        aria-hidden="true"
      >
        {n}
      </span>
      {children}
    </h2>
  );
}

const BIG_TITLE =
  "font-display text-[clamp(2.25rem,1.4rem+3vw,4.5rem)] leading-[0.95] font-extrabold tracking-[-0.03em] uppercase";

/** Card surface shared by the page: white, hairline edge, soft depth. */
const CARD = "rounded-3xl border border-border bg-card shadow-soft";

function hoursBetween(a: string, b: string) {
  return Math.max(1, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 3_600_000));
}

/** The paragraph with its key words on a strip of yellow highlighter. */
function Highlighted({ text, words }: { text: string; words: string[] }) {
  const marks = new Set(words.map((w) => w.toLowerCase()));
  return (
    <>
      {text.split(" ").map((word, i, all) => {
        const bare = word.toLowerCase().replace(/[^\p{L}\p{N}-]/gu, "");
        return (
          <Fragment key={i}>
            {marks.has(bare) ? (
              <mark className="rounded-[0.15em] bg-volt/70 px-[0.08em] text-foreground">{word}</mark>
            ) : (
              word
            )}
            {i < all.length - 1 ? " " : null}
          </Fragment>
        );
      })}
    </>
  );
}

export function AboutSection({ facts }: { facts: EventFacts }) {
  const { t, lang } = useI18n();
  const stats = [
    { value: String(SPEAKERS.length), label: t("about.speakers"), tone: "bg-paint-blue text-white" },
    {
      value: String(hoursBetween(facts.startsAt, facts.endsAt)),
      label: t("about.hours"),
      tone: "bg-paint-pink text-white",
    },
    ...(SHOW_DOORPRIZE
      ? [{ value: t("about.prizeValue"), label: t("about.prize"), tone: "bg-volt text-volt-foreground" }]
      : []),
  ];

  return (
    <section
      id="about"
      tabIndex={-1}
      aria-labelledby="about-title"
      className="relative px-5 pt-16 pb-20 outline-none sm:px-8 lg:pt-24 lg:pb-28"
    >
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,2.4fr)] lg:gap-16">
        <div className="grid content-start gap-6 lg:sticky lg:top-28 lg:self-start">
          <Kicker n="01" id="about-title">
            {t("about.kicker")}
          </Kicker>
          <Sticker name="flowerHead" className="hidden w-[min(100%,220px)] lg:block" />
        </div>
        <Reveal inView className="grid gap-10">
          <p className="text-[clamp(1.375rem,0.9rem+2.2vw,2.75rem)] leading-[1.18] font-semibold tracking-[-0.02em] text-pretty">
            <Highlighted text={ABOUT[lang]} words={ABOUT_HIGHLIGHTS[lang]} />
          </p>
          <ul
            role="list"
            className={cn("grid grid-cols-2 gap-3 sm:gap-4", stats.length === 3 && "sm:grid-cols-3")}
          >
            {stats.map((s, i) => (
              <li
                key={s.label}
                className={cn(
                  "@container grid min-w-0 content-start gap-1 rounded-3xl p-5 sm:p-6",
                  // An odd last card fills the phone row instead of standing alone.
                  stats.length % 2 === 1 && i === stats.length - 1 && "max-sm:col-span-2",
                  s.tone,
                )}
              >
                <span
                  className={cn(
                    "font-display leading-none font-extrabold tracking-tight",
                    // A word instead of a number scales with its card so it never runs past the edge.
                    /^\d+$/.test(s.value) ? "text-3xl sm:text-4xl lg:text-5xl" : "text-[min(13cqi,3rem)]",
                  )}
                >
                  {s.value}
                </span>
                <span className="text-sm font-medium">{s.label}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
      <SignalCable className="mx-auto mt-14 max-w-6xl sm:mt-20" />
    </section>
  );
}

const PILLARS: { tone: string; sticker: MuralIcon }[] = [
  { tone: "bg-paint-blue text-white", sticker: "parabola" },
  { tone: "bg-paint-pink text-white", sticker: "bujangGanong" },
  { tone: "bg-paint-teal text-white", sticker: "flowerHead" },
];

/** The three ideas of the theme as three flat painted cards in a row. */
export function ThemeSection() {
  const { t, lang } = useI18n();
  const claims = THEME_LINE[lang].split(/(?<=\.)\s+/).map((c) => c.replace(/\.$/, ""));
  return (
    <section
      id="theme"
      tabIndex={-1}
      aria-labelledby="theme-title"
      className="px-5 py-20 outline-none sm:px-8 lg:py-28"
    >
      <div className="mx-auto grid max-w-7xl gap-10">
        <div className="grid gap-4">
          <Kicker n="02" id="theme-title">
            {t("theme.kicker")}
          </Kicker>
          <p className={BIG_TITLE}>{t("theme.title")}</p>
        </div>
        <ul role="list" className="grid gap-4 sm:gap-5 lg:grid-cols-3">
          {claims.map((claim, i) => {
            const pillar = PILLARS[i % PILLARS.length] ?? { tone: "", sticker: "parabola" as MuralIcon };
            const [first, ...rest] = claim.split(" ");
            return (
              <Reveal
                as="li"
                inView
                delay={i * 0.06}
                key={claim}
                className={cn(
                  "@container relative flex min-h-64 flex-col gap-6 overflow-hidden rounded-3xl p-6 sm:min-h-80 sm:p-8 lg:min-h-96",
                  pillar.tone,
                )}
              >
                <span className="w-fit rounded-full bg-black/15 px-3 py-1 font-mono text-xs font-medium tracking-[0.16em] uppercase">
                  0{i + 1} / 0{claims.length}
                </span>
                {/* Sized to the card, not the page: the longest word ("Connecting") always fits its row. */}
                <p className="relative z-10 font-display text-[clamp(1.75rem,12.5cqi,3.5rem)] leading-[0.95] font-extrabold tracking-[-0.03em] uppercase">
                  {first}
                  <br />
                  {rest.join(" ")}
                </p>
                <Sticker
                  name={pillar.sticker}
                  className="absolute right-[-4%] bottom-[-6%] w-[34%] max-w-[180px] sm:w-[40%]"
                />
              </Reveal>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

const SPEAKER_TONES: { bg: string; sticker: MuralIcon }[] = [
  { bg: "bg-paint-blue", sticker: "burung" },
  { bg: "bg-paint-orange", sticker: "lidahMertua" },
];

function SpeakerCard({ speaker, index }: { speaker: Speaker; index: number }) {
  const { t, lang } = useI18n();
  const tone = SPEAKER_TONES[index % SPEAKER_TONES.length] ?? {
    bg: "bg-muted",
    sticker: "burung" as MuralIcon,
  };
  return (
    <Reveal as="li" inView delay={index * 0.08}>
      <article className={cn(CARD, "overflow-hidden transition-shadow duration-200 hover:shadow-lg")}>
        {speaker.photo ? (
          // Every card keeps one square frame; the photo fills it, from the top so the face stays in.
          <img
            src={speaker.photo}
            alt=""
            className="block aspect-square w-full object-cover object-top"
            loading="lazy"
          />
        ) : (
          <div className={cn("relative aspect-square overflow-hidden", tone.bg)}>
            <span
              className="absolute inset-0 grid place-items-center font-display text-[clamp(6rem,14vw,10rem)] leading-none font-extrabold tracking-[-0.06em] text-white/25"
              aria-hidden="true"
            >
              {initials(speaker.name.replace(/^(Bapak|Ibu)\s+/i, "").split(",")[0] ?? speaker.name)}
            </span>
            <span className="absolute bottom-4 left-4 rounded-full bg-card px-3 py-1 font-mono text-[0.68rem] font-medium tracking-[0.14em] uppercase">
              {t("speakers.photoSoon")}
            </span>
            <Sticker name={tone.sticker} className="absolute -right-4 -bottom-6 w-[38%]" />
          </div>
        )}
        <div className="grid gap-1.5 p-6 sm:p-7">
          <h3 className="text-2xl leading-tight font-bold tracking-tight">{speaker.name}</h3>
          <p className="text-muted-foreground">{speaker.role[lang]}</p>
          <p className="mt-1 w-fit rounded-md bg-muted px-2.5 py-1 text-sm font-semibold">
            {t(`speakers.orgs.${speaker.organisation}`)}
          </p>
        </div>
      </article>
    </Reveal>
  );
}

export function SpeakersSection() {
  const { t } = useI18n();
  return (
    <section
      id="speakers"
      tabIndex={-1}
      aria-labelledby="speakers-title"
      className="px-5 py-20 outline-none sm:px-8 lg:py-28"
    >
      <div className="mx-auto grid max-w-7xl gap-12">
        <div className="grid gap-4">
          <Kicker n="03" id="speakers-title">
            {t("speakers.kicker")}
          </Kicker>
          <p className={cn(BIG_TITLE, "max-w-[16ch]")}>{t("speakers.title")}</p>
        </div>
        <ul role="list" className="grid gap-6 md:grid-cols-2 lg:gap-10">
          {SPEAKERS.map((s, i) => (
            <SpeakerCard key={s.name} speaker={s} index={i} />
          ))}
        </ul>
      </div>
    </section>
  );
}

/** A prepaid card, drawn flat. Decorative. */
function PrizeCard({ label }: { label: string }) {
  return (
    <div
      aria-hidden="true"
      className="relative mx-auto aspect-[1.586] w-full max-w-[340px] overflow-hidden rounded-2xl bg-[linear-gradient(135deg,var(--color-paint-blue),var(--color-paint-purple))] p-5 text-white shadow-soft"
    >
      <div className="flex items-start justify-between">
        <span className="font-mono text-xs font-medium tracking-[0.18em] uppercase">{label}</span>
        <svg
          viewBox="0 0 24 24"
          className="size-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M8.5 16.5a5 5 0 0 0 0-9M12 19a9 9 0 0 0 0-14M15.5 21.5a13 13 0 0 0 0-19" />
        </svg>
      </div>
      <div className="mt-5 h-9 w-12 rounded-md bg-[linear-gradient(135deg,#ffe066,#f2c400)]" />
    </div>
  );
}

export function PerksSection() {
  const { t, lang } = useI18n();
  const merch = PERKS.find((p) => p.icon === "gift");
  const prize = SHOW_DOORPRIZE ? PERKS.find((p) => p.icon === "ticket") : undefined;
  return (
    <section
      id="perks"
      tabIndex={-1}
      aria-labelledby="perks-title"
      className="px-5 py-20 outline-none sm:px-8 lg:py-28"
    >
      <div className="mx-auto grid max-w-7xl gap-12">
        <div className="grid gap-4">
          <Kicker n="04" id="perks-title">
            {t("perks.kicker")}
          </Kicker>
          <p className={cn(BIG_TITLE, "max-w-[18ch]")}>{t("perks.title")}</p>
        </div>
        <ul role="list" className={cn("grid gap-5", merch && prize && "lg:grid-cols-[1fr_1.25fr]")}>
          {merch ? (
            <Reveal
              as="li"
              inView
              className="relative grid min-h-72 content-between gap-8 overflow-hidden rounded-3xl bg-volt p-6 text-volt-foreground sm:min-h-80 sm:p-9"
            >
              <IconGift className="size-11" aria-hidden="true" />
              <div className="relative z-10 grid max-w-sm gap-2">
                <h3 className="text-3xl leading-tight font-extrabold tracking-tight sm:text-4xl">
                  {t("perks.merchTitle")}
                </h3>
                <p className="text-base font-medium">{merch.body[lang]}</p>
              </div>
              <Sticker name="bungaUngu" className="absolute -top-4 -right-6 w-28 sm:w-44" />
            </Reveal>
          ) : null}
          {prize ? (
            <Reveal
              as="li"
              inView
              delay={0.06}
              className={cn(CARD, "grid items-center gap-8 p-6 sm:grid-cols-[1fr_1.1fr] sm:p-9")}
            >
              <div className="grid gap-2">
                <h3 className="text-3xl leading-tight font-extrabold tracking-tight sm:text-4xl">
                  {t("perks.prizeTitle")}
                </h3>
                <p className="text-base text-muted-foreground">{prize.body[lang]}</p>
              </div>
              <PrizeCard label={t("perks.prizeCard")} />
            </Reveal>
          ) : null}
        </ul>
      </div>
    </section>
  );
}

/**
 * Where the event is: the venue from `GET /v1/sessions` and a map of it. The
 * whole map is one link that opens the place in Google Maps; the embed itself
 * takes no clicks, so a tap never gets stuck panning a frame.
 */
export function VenueSection({ facts }: { facts: EventFacts }) {
  const { t } = useI18n();
  const place = [facts.venueName, facts.venueAddress].filter(Boolean).join(", ");
  const query = encodeURIComponent(place);
  const href = facts.mapUrl || `https://www.google.com/maps/search/?api=1&query=${query}`;

  return (
    <section
      id="venue"
      tabIndex={-1}
      aria-labelledby="venue-title"
      className="px-5 pb-20 outline-none sm:px-8 lg:pb-28"
    >
      <div className="mx-auto grid max-w-7xl gap-12">
        <div className="grid gap-4">
          <Kicker n="05" id="venue-title">
            {t("venue.kicker")}
          </Kicker>
          <p className={cn(BIG_TITLE, "max-w-[18ch]")}>{t("venue.title")}</p>
        </div>
        <Reveal inView className={cn(CARD, "grid gap-5 p-3 sm:p-4")}>
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={t("venue.openMapLabel", { place })}
            className="group relative block aspect-[4/3] overflow-hidden rounded-2xl bg-muted outline-none focus-visible:ring-4 focus-visible:ring-volt sm:aspect-[16/9] lg:aspect-[21/9]"
          >
            <iframe
              src={`https://www.google.com/maps?q=${query}&output=embed`}
              title={t("venue.mapTitle")}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              tabIndex={-1}
              aria-hidden="true"
              className="pointer-events-none size-full border-0"
            />
            <span className="absolute right-3 bottom-3 inline-flex items-center gap-1.5 rounded-full bg-card px-3.5 py-2 text-sm font-semibold shadow-soft transition-transform duration-200 group-hover:-translate-y-0.5">
              {t("venue.openMap")}
              <IconOpen className="size-4" aria-hidden="true" />
            </span>
          </a>
          <div className="flex items-start gap-3 px-2 pb-2 sm:px-3 sm:pb-3">
            <span
              className="grid size-10 shrink-0 place-items-center rounded-full bg-paint-pink text-white"
              aria-hidden="true"
            >
              <IconMap className="size-5" />
            </span>
            <div className="grid min-w-0 gap-0.5">
              <p className="text-xl leading-snug font-bold tracking-tight sm:text-2xl">{facts.venueName}</p>
              {facts.venueAddress ? <p className="text-muted-foreground">{facts.venueAddress}</p> : null}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/** The last call: the invitation on a clean card, set on a window of the full mural. */
export function ClosingBand() {
  const { t } = useI18n();
  const phone = CONTACT.phone;
  const wa = phone ? `https://wa.me/${phone.replace(/\D/g, "").replace(/^0/, "62")}` : null;

  return (
    <section aria-labelledby="closing-title" className="px-3 pb-3 sm:px-5 sm:pb-5">
      <div className="relative isolate grid min-h-[80svh] place-items-center overflow-hidden rounded-[2rem] px-4 py-16 sm:min-h-[90svh] sm:rounded-[2.5rem] sm:px-8 sm:py-20">
        <img
          src={MURAL_WALL}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          draggable={false}
          className="absolute inset-0 -z-10 size-full object-cover"
        />
        <Reveal
          inView
          className={cn(CARD, "grid w-full max-w-xl min-w-0 justify-items-start gap-6 p-6 sm:p-10")}
        >
          <Kicker n="06">{t("closing.kicker")}</Kicker>
          <p
            id="closing-title"
            className="font-display text-[clamp(2.25rem,1.6rem+3.6vw,4.5rem)] leading-[0.92] font-extrabold tracking-[-0.035em] uppercase"
          >
            {t("closing.title")}
          </p>
          <p className="text-lg text-muted-foreground">{t("closing.body")}</p>
          <Button asChild variant="volt" size="xl">
            <Link to="/register">
              {t("hero.register")}
              <IconArrow aria-hidden="true" />
            </Link>
          </Button>
          <div className="grid w-full gap-1 border-t border-border pt-5 text-sm">
            <p className="font-medium">
              {phone
                ? t("contact.bodyWithPhone", { name: CONTACT.name, phone })
                : t("contact.body", { name: CONTACT.name })}
            </p>
            {wa ? (
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-fit items-center gap-2 underline decoration-volt decoration-[3px] underline-offset-4"
              >
                <IconChat className="size-5" aria-hidden="true" />
                {t("contact.whatsapp")}
              </a>
            ) : null}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
